import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { getToken, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PortalSwitcher from "@/components/PortalSwitcher";
import { ScoreCards } from "@/components/ScoreCards";
import AlertPreferences from "@/components/AlertPreferences";
import { ClaimCrmPanel } from "@/components/EnterpriseCrm";
import {
  ShieldCheck, LogOut, Loader2, MapPin, Calendar, Camera, Film, FileText, Car,
  Stethoscope, Store, X, Check, Trophy, Activity, Scale, Download,
  CheckCircle2, Eye, FolderDown, DatabaseZap, MessageSquare, Zap, Clock, BellRing,
} from "lucide-react";

const timeAgo = (iso) => {
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

function MarketplaceActivityFeed({ onOpen }) {
  const [items, setItems] = useState([]);
  const seen = useRef(null);
  const first = useRef(true);
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get("/marketplace/activity", { params: { limit: 30 } });
        if (!mounted) return;
        const top = data[0];
        if (!first.current && top && top.activity_id !== seen.current && top.kind === "listed" && top.available) {
          const loc = [top.city, top.state].filter(Boolean).join(", ") || "Marketplace";
          const grade = top.overall_grade ? `Score ${top.overall_grade} · ` : "";
          toast(`New case listed · ${loc}`, {
            description: `${grade}Claim fee $${Number(top.access_fee).toLocaleString()} — first to claim wins.`,
            icon: <Zap className="w-4 h-4 text-cobalt" />,
            action: { label: "Claim", onClick: () => onOpen(top.case_id) },
            duration: 12000,
          });
        }
        if (top) seen.current = top.activity_id;
        first.current = false;
        setItems(data);
      } catch { /* ignore */ }
    };
    load();
    const t = setInterval(load, 10000);
    return () => { mounted = false; clearInterval(t); };
  }, [onOpen]);

  if (!items.length) return null;
  return (
    <div className="mt-6 bg-white rounded-3xl border border-slate-100 p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="marketplace-activity-feed">
      <div className="flex items-center gap-2">
        <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cobalt opacity-60" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-cobalt" /></span>
        <h2 className="font-heading font-extrabold text-navy">Marketplace Activity</h2>
        <span className="text-xs text-slate-400">· live · first to claim wins</span>
      </div>
      <div className="mt-3 space-y-2 max-h-72 overflow-y-auto pr-1">
        {items.map((a) => {
          const loc = [a.city, a.state].filter(Boolean).join(", ") || "—";
          const claimed = a.kind === "claimed" || (a.kind === "listed" && a.available === false);
          return (
            <div key={a.activity_id} data-testid={`activity-${a.activity_id}`}
              className={`flex items-center justify-between gap-3 rounded-2xl border px-4 py-2.5 ${claimed ? "border-slate-100 bg-slate-50" : "border-cobalt/20 bg-cobalt-light/40"}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 ${claimed ? "bg-slate-200" : "bg-cobalt/15"}`}>
                  {a.kind === "claimed" ? <CheckCircle2 className="w-5 h-5 text-slate-400" /> : <Zap className={`w-5 h-5 ${claimed ? "text-slate-400" : "text-cobalt"}`} />}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-navy truncate">
                    {a.kind === "claimed" ? `Claimed by ${a.claimed_by_name || "an attorney"}` : "New case listed"}
                    <span className="text-slate-400 font-normal"> · {loc}</span>
                  </div>
                  <div className="text-xs text-slate-500 flex items-center gap-2">
                    {a.overall_grade && <span className="font-semibold" style={{ color: a.overall_color || "#0a2540" }}>Score {a.overall_grade} ({a.overall_score})</span>}
                    <span className="text-cobalt font-semibold">${Number(a.access_fee).toLocaleString()}</span>
                    <span className="flex items-center gap-1 text-slate-400"><Clock className="w-3 h-3" /> {timeAgo(a.at)}</span>
                  </div>
                </div>
              </div>
              {a.kind === "listed" && a.available ? (
                <button onClick={() => onOpen(a.case_id)} data-testid={`activity-claim-${a.case_id}`} className="shrink-0 flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-cobalt text-white text-xs font-semibold hover:bg-cobalt-hover active:scale-95 transition-all"><Eye className="w-3.5 h-3.5" /> View & Claim</button>
              ) : (
                <span className="shrink-0 text-xs font-medium text-slate-400">Claimed</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const SORTS = [
  { key: "newest", label: "Newest" }, { key: "oldest", label: "Oldest" },
  { key: "case_score", label: "Highest Case Score" }, { key: "injury_score", label: "Highest Injury" },
  { key: "liability_score", label: "Highest Liability" },
];

const GradePill = ({ label, s }) => (
  <div className="flex flex-col items-center">
    <div className="text-[10px] font-semibold text-slate-400 uppercase">{label}</div>
    <div className="font-heading font-extrabold text-lg" style={{ color: s?.color || "#94a3b8" }}>{s?.grade || "—"}</div>
    <div className="text-[10px] text-slate-400">{s?.score ?? "—"}</div>
  </div>
);

const PremiumBadge = ({ badge, size = "sm" }) => {
  if (!badge?.tier) return null;
  const gold = badge.tier === "gold";
  const cls = gold
    ? "bg-gradient-to-r from-amber-400 to-yellow-500 text-white border-amber-300"
    : "bg-gradient-to-r from-slate-300 to-slate-400 text-white border-slate-200";
  return (
    <span data-testid={`premium-badge-${badge.tier}`}
      className={`inline-flex items-center gap-1 rounded-full border font-bold shadow-sm ${cls} ${size === "sm" ? "px-2 py-0.5 text-[10px]" : "px-3 py-1 text-xs"}`}
      title={`${badge.count}/${badge.total}: ${(badge.met || []).join(", ")}`}>
      <Trophy className={size === "sm" ? "w-3 h-3" : "w-3.5 h-3.5"} /> {gold ? "GOLD" : "SILVER"} · {badge.count}/{badge.total}
    </span>
  );
};


export default function Marketplace() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("case_score");
  const [state, setState] = useState("");
  const [commercial, setCommercial] = useState(false);
  const [preview, setPreview] = useState(null);
  const [claiming, setClaiming] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showPrefs, setShowPrefs] = useState(false);
  const [filters, setFilters] = useState({ min_trust: "", rn_verified: false, insurance_verified: false, id_verified: false, police_report: false, drone_approved: false, drone_completed: false, evidence_complete: false, collision: "" });

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = { sort };
      if (state) params.state = state.toUpperCase();
      if (commercial) params.commercial = true;
      if (filters.min_trust) params.min_trust = Number(filters.min_trust);
      if (filters.collision) params.collision = filters.collision;
      ["rn_verified", "insurance_verified", "id_verified", "police_report", "drone_approved", "drone_completed", "evidence_complete"].forEach((k) => { if (filters[k]) params[k] = true; });
      const { data } = await api.get("/marketplace", { params });
      setCards(data);
    } catch { if (!silent) toast.error("Could not load marketplace"); }
    finally { if (!silent) setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sort, commercial, filters]);
  useEffect(() => { const t = setTimeout(load, 400); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [state]);
  useEffect(() => { const t = setInterval(() => load(true), 15000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [sort, commercial, state]);

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-xl bg-navy flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-white" strokeWidth={2.2} /></div>
            <span className="font-heading font-extrabold text-lg tracking-tight text-navy">Crash Ready <span className="text-cobalt">Marketplace</span></span>
          </div>
          <div className="flex items-center gap-3">
            <PortalSwitcher />
            <button onClick={doLogout} data-testid="marketplace-logout" className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 transition-all"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2"><Store className="w-5 h-5 text-cobalt" /><p className="text-xs font-medium text-slate-500 uppercase tracking-widest">Available Clients</p></div>
            <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-navy mt-1">Client Marketplace</h1>
            <p className="mt-2 text-slate-600 max-w-2xl">Real, consented clients with verified evidence. Preview the case, then claim to unlock contact details and the full file.</p>
          </div>
          <button onClick={() => setShowPrefs(true)} data-testid="alert-prefs-btn" className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-slate-200 text-sm font-medium text-navy hover:border-cobalt hover:text-cobalt transition-all shadow-sm"><BellRing className="w-4 h-4" /> Alert Preferences</button>
        </div>

        <MarketplaceActivityFeed onOpen={(id) => setPreview(id)} />

        {/* Filters */}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="market-sort" className="bg-white border border-slate-200 rounded-full px-4 py-2.5 text-sm font-medium outline-none focus:border-cobalt">
            {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <input value={state} onChange={(e) => setState(e.target.value)} data-testid="market-state" placeholder="State (e.g. NC)" maxLength={2} className="w-32 bg-white border border-slate-200 rounded-full px-4 py-2.5 text-sm outline-none focus:border-cobalt uppercase" />
          <button onClick={() => setCommercial((v) => !v)} data-testid="market-commercial" className={`px-4 py-2.5 rounded-full text-sm font-medium border transition-all ${commercial ? "bg-cobalt text-white border-cobalt" : "bg-white border-slate-200 text-slate-600"}`}>Commercial Vehicle</button>
          {[["rn_verified", "RN Verified"], ["insurance_verified", "Insurance Verified"], ["id_verified", "ID Verified"],
            ["police_report", "Police Report"], ["drone_approved", "Drone Approved"], ["drone_completed", "Drone Completed"],
            ["evidence_complete", "Evidence Complete"]].map(([k, label]) => (
            <button key={k} onClick={() => setFilters((f) => ({ ...f, [k]: !f[k] }))} data-testid={`market-filter-${k}`}
              className={`px-4 py-2.5 rounded-full text-sm font-medium border transition-all ${filters[k] ? "bg-cobalt text-white border-cobalt" : "bg-white border-slate-200 text-slate-600"}`}>{label}</button>
          ))}
          <input value={filters.min_trust} onChange={(e) => setFilters((f) => ({ ...f, min_trust: e.target.value.replace(/\D/g, "") }))} data-testid="market-min-trust"
            placeholder="Min Trust" className="w-28 bg-white border border-slate-200 rounded-full px-4 py-2.5 text-sm outline-none focus:border-cobalt" />
          <input value={filters.collision} onChange={(e) => setFilters((f) => ({ ...f, collision: e.target.value }))} data-testid="market-collision"
            placeholder="Accident type" className="w-36 bg-white border border-slate-200 rounded-full px-4 py-2.5 text-sm outline-none focus:border-cobalt" />
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div>
        ) : cards.length === 0 ? (
          <div className="mt-8 bg-white rounded-3xl border border-dashed border-slate-200 p-12 text-center">
            <Store className="w-12 h-12 text-slate-300 mx-auto" />
            <p className="mt-3 text-slate-500">No available clients match your filters right now.</p>
          </div>
        ) : (
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {cards.map((c) => (
              <button key={c.case_id} onClick={() => setPreview(c.case_id)} data-testid={`market-card-${c.case_id}`}
                className="text-left bg-white rounded-3xl border border-slate-100 p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-0.5 hover:shadow-lg transition-all">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-heading font-bold text-navy text-lg">{c.display_name}</div>
                    <div className="text-xs text-slate-400 font-mono">{c.case_number}</div>
                  </div>
                  {c.overall?.grade && <div className="h-12 w-12 rounded-2xl flex items-center justify-center text-white font-heading font-extrabold" style={{ backgroundColor: c.overall.color }}>{c.overall.grade}</div>}
                </div>
                {c.rear_end_priority && (
                  <div className="mt-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 w-fit" data-testid={`rear-end-priority-${c.case_id}`}>
                    <Zap className="w-3 h-3" /> {c.rear_end_priority_label}
                  </div>
                )}
                {c.verification_summary?.crash_ready_verified && (
                  <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1" data-testid={`cr-verified-badge-${c.case_id}`}>
                    <ShieldCheck className="w-3 h-3" /> Crash Ready Verified
                  </div>
                )}
                {c.premium_badge?.tier && <div className="mt-2"><PremiumBadge badge={c.premium_badge} /></div>}
                <div className="mt-4 grid grid-cols-4 gap-1 bg-slate-50 rounded-2xl py-3">
                  <GradePill label="Overall" s={c.overall} />
                  <GradePill label="Injury" s={c.injury} />
                  <GradePill label="Coverage" s={c.coverage} />
                  <GradePill label="Liability" s={c.liability} />
                </div>
                <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {[c.city, c.state].filter(Boolean).join(", ") || "—"}</span>
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(c.accident_date).toLocaleDateString()}</span>
                  {c.collision_type && <span className="flex items-center gap-1"><Car className="w-3 h-3" /> {c.collision_type}</span>}
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {c.commercial_vehicle && <Tag>Commercial</Tag>}
                  {c.rn_vetted && <Tag><Stethoscope className="w-3 h-3" /> RN Vetted</Tag>}
                  {c.treatment_started && <Tag><Stethoscope className="w-3 h-3" /> Treatment</Tag>}
                  {c.police_report_uploaded && <Tag><FileText className="w-3 h-3" /> Police Report</Tag>}
                  <Tag><Camera className="w-3 h-3" /> {c.photos}</Tag>
                  <Tag><Film className="w-3 h-3" /> {c.videos}</Tag>
                </div>
              </button>
            ))}
          </div>
        )}
      </main>

      {preview && <PreviewModal caseId={preview} onClose={() => setPreview(null)} onClaim={(id, fee) => { setPreview(null); setClaiming({ id, fee }); }} />}
      {claiming && <ClaimModal caseId={claiming.id} fee={claiming.fee} onClose={() => setClaiming(null)} onDone={(res) => { setSuccess({ caseId: claiming.id, ...res }); setClaiming(null); load(); }} />}
      {success && <SuccessModal data={success} onClose={() => setSuccess(null)} />}
      {showPrefs && <AlertPreferences onClose={() => setShowPrefs(false)} />}
    </div>
  );
}

const Tag = ({ children }) => <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[11px] font-medium">{children}</span>;

function PreviewModal({ caseId, onClose, onClaim }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/marketplace/${caseId}`).then(({ data }) => setData(data)).catch(() => { toast.error("Already claimed or unavailable"); onClose(); }); /* eslint-disable-next-line */ }, [caseId]);
  return (
    <div className="fixed inset-0 z-50 bg-navy/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="preview-modal">
        {!data ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div> : (
          <div className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs font-mono text-slate-400">{data.card.case_number}</div>
                <h2 className="font-heading text-2xl font-extrabold text-navy">{data.card.display_name}</h2>
                <div className="text-sm text-slate-500 mt-1">{[data.city, data.state].filter(Boolean).join(", ")} · {new Date(data.accident_date).toLocaleDateString()}</div>
                {data.card.premium_badge?.tier && <div className="mt-2"><PremiumBadge badge={data.card.premium_badge} size="lg" /></div>}
              </div>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
            </div>

            {data.scores && <div className="mt-5"><ScoreCards scores={data.scores} /></div>}

            <div className="mt-5 grid sm:grid-cols-2 gap-4">
              <Section title="Accident">
                <Row k="Collision type" v={data.collision_type} />
                <Row k="Vehicle" v={data.vehicle} />
                <Row k="Property damage" v={data.property_damage} />
                <Row k="Insurance type" v={data.insurance_type} />
                <Row k="Police report #" v={data.police_report_number} />
              </Section>
              <Section title="Injuries (Body Map)">
                {data.injuries?.length ? data.injuries.map((i, idx) => (
                  <div key={idx} className="text-sm py-1 border-b border-slate-50 last:border-0">
                    <span className="font-medium text-navy capitalize">{i.type.replace(/_/g, " ")}</span>
                    {i.locations?.length > 0 && <span className="text-slate-500"> — {i.locations.join(", ")}</span>}
                  </div>
                )) : <div className="text-sm text-slate-400">No injuries reported</div>}
              </Section>
              <Section title="Treatment Timeline">
                {data.medical?.length ? data.medical.map((m, idx) => <Row key={idx} k={m.type || "Treatment"} v={[m.provider, m.date].filter(Boolean).join(" · ")} />) : <div className="text-sm text-slate-400">No treatment logged</div>}
              </Section>
              <Section title="Evidence">
                <Row k="Photos" v={data.evidence_counts.photos} />
                <Row k="Videos" v={data.evidence_counts.videos} />
                <Row k="Total files" v={data.evidence_counts.total} />
              </Section>
            </div>

            {(data.card.verification_summary && !data.card.verification_summary.legacy) && (
              <div className="mt-5 rounded-2xl border border-slate-100 bg-white p-4" data-testid="verification-summary-panel">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-cobalt" />
                    <span className="font-heading font-semibold text-navy text-sm">Verification Summary</span>
                    {data.crash_ready_verified?.achieved && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="preview-cr-verified">
                        <ShieldCheck className="w-3 h-3" /> Crash Ready Verified
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-slate-500">
                    Trust {data.card.verification_summary.trust_score ?? "—"} · {data.card.verification_summary.trust_class || ""}
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5">
                  {[["phone_verified", "Phone verified"], ["gps_verified", "GPS verified"], ["live_evidence", "Live evidence captured"],
                    ["driver_id_verified", "Driver ID verified"], ["insurance_verified", "Insurance verified"],
                    ["rn_verified", "RN verification completed"], ["storyline_reviewed", "Storyline reviewed"],
                    ["permanent_evidence", "Permanent Evidence Locker"], ["police_report", "Police report"]].map(([k, label]) => (
                    <div key={k} className="flex items-center gap-1.5 text-xs" data-testid={`vsum-${k}`}>
                      {data.card.verification_summary[k]
                        ? <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                        : <X className="w-3.5 h-3.5 text-slate-300 shrink-0" />}
                      <span className={data.card.verification_summary[k] ? "text-slate-600" : "text-slate-400"}>{label}</span>
                    </div>
                  ))}
                  <div className="flex items-center gap-1.5 text-xs" data-testid="vsum-drone">
                    <Zap className="w-3.5 h-3.5 text-cobalt shrink-0" />
                    <span className="text-slate-600">Drone: {data.drone_dispatch_status ? data.drone_dispatch_status.replace(/_/g, " ") : (data.card.verification_summary.drone_decision ? data.card.verification_summary.drone_decision.replace(/_/g, " ") : "not requested")}</span>
                  </div>
                </div>
                <p className="mt-3 text-[10px] text-slate-400">{data.crash_ready_verified?.disclaimer || "This summary documents completed verification steps only. It does not guarantee liability, case value, insurance coverage, injury causation, attorney acceptance or settlement."}</p>
              </div>
            )}

            <div className="mt-5 rounded-2xl bg-amber-50 text-amber-700 text-xs p-3">Contact details (name, phone, email, address) are hidden until you claim this client.</div>

            <div className="mt-3 rounded-2xl bg-cobalt-light px-4 py-3" data-testid="preview-fee">
              {Number(data.card?.premium_fee) > 0 && (
                <div className="mb-2 space-y-1 text-sm text-navy border-b border-cobalt/20 pb-2" data-testid="preview-fee-breakdown">
                  <div className="flex items-center justify-between"><span>Base claim fee</span><span>${Number(data.card.base_fee).toLocaleString()}</span></div>
                  <div className="flex items-center justify-between text-amber-600 font-semibold"><span className="flex items-center gap-1"><Trophy className="w-3.5 h-3.5" /> RN Vetted premium</span><span>+${Number(data.card.premium_fee).toLocaleString()}</span></div>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-navy">{Number(data.card?.premium_fee) > 0 ? "Total claim fee" : "Claim / Access Fee"}</span>
                <span className="font-heading text-xl font-extrabold text-cobalt">${Number(data.access_fee ?? data.card?.access_fee ?? 100).toLocaleString()}</span>
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button onClick={onClose} className="px-5 py-3 rounded-full bg-white border border-slate-200 text-slate-700 font-medium hover:bg-slate-50">Close</button>
              <button onClick={() => onClaim(caseId, data.access_fee)} data-testid="open-claim-btn" className="flex items-center gap-2 px-6 py-3 rounded-full bg-cobalt text-white font-medium hover:bg-cobalt-hover transition-all active:scale-95"><Download className="w-4 h-4" /> Claim Client (Download / Import)</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ClaimModal({ caseId, fee = 100, onClose, onDone }) {
  const [terms, setTerms] = useState(false);
  const [purchase, setPurchase] = useState(false);
  const [invoice, setInvoice] = useState(false);
  const [busy, setBusy] = useState(false);
  const all = terms && purchase && invoice;
  const feeLabel = `$${Number(fee).toLocaleString()}`;

  const claim = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/marketplace/${caseId}/claim`, { accept_terms: terms, accept_purchase: purchase, accept_invoice: invoice });
      onDone(data);
    } catch (e) {
      const detail = formatApiErrorDetail(e?.response?.data?.detail) || "Claim failed. If you were not charged, please try again.";
      toast.error(detail);
      // Persist the failure with case, attorney, timestamp, device & error (server adds IP + device).
      try {
        await api.post(`/cases/${caseId}/log-client-event`, { context: "claim_client", error: String(detail), device: navigator.userAgent });
      } catch { /* non-blocking */ }
      setBusy(false);
    }
  };

  const Check1 = ({ v, set, label }) => (
    <button type="button" onClick={() => set(!v)} className={`w-full text-left flex items-start gap-3 px-4 py-3 rounded-2xl border transition-all ${v ? "border-cobalt bg-cobalt-light" : "border-slate-200"}`}>
      <span className={`h-5 w-5 rounded-md border-2 flex items-center justify-center shrink-0 mt-0.5 ${v ? "border-cobalt bg-cobalt" : "border-slate-300"}`}>{v && <Check className="w-3 h-3 text-white" />}</span>
      <span className="text-sm text-slate-700">{label}</span>
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 bg-navy/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="claim-modal">
        <h2 className="font-heading text-2xl font-extrabold text-navy">Claim this Client</h2>
        <p className="mt-2 text-sm text-slate-600">An <b>Initial Access Fee of {feeLabel}</b> is charged immediately (receipt generated). The remaining referral balance is added to your invoices with no due date — pay when ready. Claiming removes this client from all other attorneys.</p>
        <ClaimCrmPanel />
        <div className="mt-5 space-y-2">
          <Check1 v={terms} set={setTerms} label="I accept the Terms of Service." />
          <Check1 v={purchase} set={setPurchase} label="I accept the Purchase Agreement for this digital case file." />
          <Check1 v={invoice} set={setInvoice} label="I accept the Invoice Terms (Initial Access Fee + remaining balance)." />
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="px-5 py-3 rounded-full bg-white border border-slate-200 text-slate-700 font-medium hover:bg-slate-50">Cancel</button>
          <button onClick={claim} disabled={!all || busy} data-testid="confirm-claim-btn" className="flex items-center gap-2 px-6 py-3 rounded-full bg-cobalt text-white font-medium hover:bg-cobalt-hover transition-all active:scale-95 disabled:opacity-50">{busy ? "Processing…" : `Pay ${feeLabel} & Claim`}</button>
        </div>
      </div>
    </div>
  );
}

function SuccessModal({ data, onClose }) {
  const navigate = useNavigate();
  const token = getToken();
  const exportUrl = `${process.env.REACT_APP_BACKEND_URL}/api/cases/${data.caseId}/export?auth=${token}`;
  const go = (focus) => { onClose(); navigate(`/attorney?case=${data.caseId}${focus ? `&focus=${focus}` : ""}`); };

  const Action = ({ icon: Icon, label, onClick, href, testid }) => {
    const cls = "flex items-center gap-3 w-full px-4 py-3.5 rounded-2xl border border-slate-200 bg-white hover:border-cobalt hover:bg-cobalt-light transition-all text-left";
    const inner = <><span className="h-9 w-9 rounded-xl bg-cobalt-light flex items-center justify-center shrink-0"><Icon className="w-4.5 h-4.5 text-cobalt" /></span><span className="text-sm font-semibold text-navy">{label}</span></>;
    return href
      ? <a href={href} download onClick={() => toast.success("Case file downloading…")} data-testid={testid} className={cls}>{inner}</a>
      : <button onClick={onClick} data-testid={testid} className={cls}>{inner}</button>;
  };

  return (
    <div className="fixed inset-0 z-50 bg-navy/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-md w-full p-6 text-center" onClick={(e) => e.stopPropagation()} data-testid="claim-success-modal">
        <div className="h-16 w-16 rounded-full bg-green-50 flex items-center justify-center mx-auto"><CheckCircle2 className="w-9 h-9 text-green-500" /></div>
        <h2 className="font-heading text-2xl font-extrabold text-navy mt-4">Client Claimed</h2>
        <p className="mt-1 text-sm text-slate-600">You now have exclusive access to this case. Contact details are unlocked and the client has been notified.</p>
        <div className="mt-4 rounded-2xl bg-slate-50 p-3 text-xs text-slate-600 space-y-1">
          <div>Transaction: <span className="font-mono text-navy" data-testid="success-txn">{data.transaction_id}</span></div>
          <div>Access fee paid: <b className="text-navy">${Number(data.access_fee || 0).toLocaleString()}</b> · Balance invoiced: <b className="text-navy">${Number(data.balance || 0).toLocaleString()}</b></div>
        </div>
        <div className="mt-5 grid gap-2.5">
          <Action icon={Eye} label="View Full Case File" onClick={() => go()} testid="success-view-case" />
          <Action icon={FolderDown} label="Download Case File" href={exportUrl} testid="success-download-case" />
          <Action icon={DatabaseZap} label="Import Into My CRM" onClick={() => go("crm")} testid="success-import-crm" />
          <Action icon={MessageSquare} label="Start Secure Chat" onClick={() => go("chat")} testid="success-start-chat" />
        </div>
        <button onClick={onClose} className="mt-4 text-sm text-slate-400 hover:text-slate-600">Close</button>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="bg-white rounded-2xl border border-slate-100 p-4">
    <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">{title}</div>
    {children}
  </div>
);
const Row = ({ k, v }) => (
  <div className="flex justify-between py-1.5 border-b border-slate-50 last:border-0 text-sm">
    <span className="text-slate-500">{k}</span><span className="text-navy font-medium text-right">{v || "—"}</span>
  </div>
);
